import { useTranslation } from "react-i18next";
import cn from "classnames";
import { Add } from "iconsax-react";
import type { TaskListItem, TaskStatus } from "../../../types";
import type { ProjectBoardView } from "../../../hooks/useCrossProjectBoard";
import { TASK_STATUS_MAP, COLUMN_HEADER_STYLES } from "../../../consts";
import ProjectKanbanRow from "./ProjectKanbanRow";

const EMPTY_STATUSES: TaskStatus[] = ["todo", "in_progress", "blocked", "done"];

interface GeneralKanbanViewProps {
  projectBoards: ProjectBoardView[];
  onTaskClick: (task: TaskListItem, projectId: number) => void;
  onCreateTask?: (status: TaskStatus, projectId: number) => void;
  onReorderError: () => void;
  canReorder: boolean;
  canDragAll: boolean;
  currentUserId: number | null;
  isBacklogActive: boolean;
  onNavigateToProject?: (projectId: number) => void;
}

export default function GeneralKanbanView({
  projectBoards,
  onTaskClick,
  onCreateTask,
  onReorderError,
  canReorder,
  canDragAll,
  currentUserId,
  isBacklogActive,
  onNavigateToProject,
}: GeneralKanbanViewProps) {
  const { t: tTasks } = useTranslation("TasksPage");
  const { t: tBoard } = useTranslation("BoardPage");
  const showProjectHeadings = projectBoards.length > 1;

  const emptyGridColumns = `repeat(${EMPTY_STATUSES.length}, minmax(200px, 1fr))`;

  if (projectBoards.length === 0) {
    return (
      <div className="overflow-x-auto min-h-[120px]">
        <div
          className="grid pb-2 border-b surface-component-stroke gap-x-5"
          style={{ gridTemplateColumns: emptyGridColumns }}>
          {EMPTY_STATUSES.map((status) => {
            const styles = COLUMN_HEADER_STYLES[status];
            return (
              <div key={status}>
                <div className="flex justify-center items-center gap-1.5">
                  <p className="text-body-bold-md content-base-primary">{tTasks(TASK_STATUS_MAP[status].label)}</p>
                  <span
                    className={cn(
                      "w-6.5 h-5 radius-2xs flex justify-center items-center text-label-xs",
                      styles.badge,
                    )}>
                    0
                  </span>
                </div>
              </div>
            );
          })}
        </div>
        {onCreateTask && (
          <div className="grid mt-3 gap-x-5" style={{ gridTemplateColumns: emptyGridColumns }}>
            {EMPTY_STATUSES.map((status) => (
              <button
                key={status}
                type="button"
                onClick={() => onCreateTask(status, 0)}
                className="w-full h-6 radius-2xs border border-dashed surface-component-stroke flex justify-center items-center gap-1 content-base-low cursor-pointer">
                <Add size={12} color="currentColor" />
                <p className="text-label-xs content-base-low">{tBoard("kanban.addTask")}</p>
              </button>
            ))}
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="flex flex-col">
      {projectBoards.map((board, index) => (
        <ProjectKanbanRow
          key={board.project.id}
          board={board}
          isEven={index % 2 === 1}
          onTaskClick={onTaskClick}
          onCreateTask={onCreateTask}
          onReorderError={onReorderError}
          canReorder={canReorder}
          canDragAll={canDragAll}
          currentUserId={currentUserId}
          isBacklogActive={isBacklogActive}
          onNavigateToProject={onNavigateToProject}
          showHeading={showProjectHeadings}
        />
      ))}
    </div>
  );
}
